import { TLayoutItem } from '@redux/layouts/actions';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import { CHANNELS_FILTER } from 'constants/constants';
import RestList from 'containers/rest/List';
import { useMemo } from 'react';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import { useNavigate } from 'react-router-dom';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import { getColumns } from './utils';
import { LabelValuePairsType } from 'core/types/common';
import LocationsFilter from '../components/Filters/Locations';
import {
  FAILED_QC_REASON,
  RETURN_CHANNEL,
  RETURN_METHOD,
  RETURN_REASON,
  RETURN_STATUS,
} from 'constants/ordersReturn';
import { useTranslation } from 'react-i18next';
import RangePickerFilter from '../components/RangePickerFilter';
import dayjs from 'dayjs';
import PaymentTypeFilter from '../components/Filters/PaymentType';
import { upperFirst } from 'lodash';
import { getQueryString } from 'api/utils';
import { convertRequestParams } from '@redux/crudCreator/dataProvider';
import values from 'lodash/values';
import omit from 'lodash/omit';
import { newCheckRole } from 'utils/tools';
import { useSelector } from '@redux';

const DATA_FIELD_SEARCH: LabelValuePairsType = [
  {
    label: 'Return Request No',
    value: 'returnNumber',
  },
  {
    label: 'Order No',
    value: 'orderNumber',
  },
  // {
  //    label: 'SO',
  //    value: 'saleOrderId'
  // },
  {
    label: 'SKU',
    value: 'sku',
  },
  {
    label: 'Shipping Phone',
    value: 'telephone',
  },
  {
    label: 'Email',
    value: 'email',
  },
  {
    label: 'Customer name',
    value: 'customerName',
  },
  {
    label: 'Shipping number',
    value: 'deliveryCode',
  },
];

const FILTERS_OBJECT = {
  AWAITING_APPROVAL: {
    condition: 'and',
    value: RETURN_STATUS['AWAITING_APPROVAL'].value,
    operator: 'is',
    key: 'status',
  },
  APPROVED: {
    condition: 'and',
    value: RETURN_STATUS['APPROVED'].value,
    operator: 'is',
    key: 'status',
  },
  // PASS_QC: {
  //   condition: 'and',
  //   value: RETURN_STATUS['PASS_QC'].value,
  //   operator: 'is',
  //   key: 'status',
  // },
  FAILED_QC: {
    condition: 'and',
    value: RETURN_STATUS['FAILED_QC'].value,
    operator: 'is',
    key: 'status',
  },
  WAITING_FOR_REFUND: {
    condition: 'and',
    value: RETURN_STATUS['WAITING_FOR_REFUND'].value,
    operator: 'is',
    key: 'status',
  },
  COMPLETED: {
    condition: 'and',
    value: RETURN_STATUS['COMPLETED'].value,
    operator: 'is',
    key: 'status',
  },
  CANCELLED: {
    condition: 'and',
    value: RETURN_STATUS['CANCELLED'].value,
    operator: 'is',
    key: 'status',
  },
};

const ROW_SELECTION_ACTIONS = <></>;
const EXTRA_ACTIONS_FILTER = <RangePickerFilter />;

export default function List() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filter, searchParams } = useGetFilter();
  const user = useSelector<DynamicObjectType>((state) => state.auth.data);

  const INITIAL_LAYOUTS: TLayoutItem[] = useMemo(() => {
    return [
      {
        rootPath: '/orders-return',
        pathId: RETURN_STATUS['AWAITING_APPROVAL'].value,
        title: 'Awaiting Approval',
        customPath: `/orders-return?customLayout=${RETURN_STATUS['AWAITING_APPROVAL'].value}&filters=${encodeURIComponent(
          JSON.stringify([FILTERS_OBJECT['AWAITING_APPROVAL']], null, 0),
        )}`,
        isDefault: true,
      },
      {
        rootPath: '/orders-return',
        pathId: RETURN_STATUS['APPROVED'].value,
        title: 'Approved',
        customPath: `/orders-return?customLayout=${RETURN_STATUS['APPROVED'].value}&filters=${encodeURIComponent(
          JSON.stringify([FILTERS_OBJECT['APPROVED']], null, 0),
        )}`,
        isDefault: true,
      },
      // {
      //   rootPath: '/orders-return',
      //   pathId: RETURN_STATUS['PASS_QC'].value,
      //   title: 'Passed QC',
      //   customPath: `/orders-return?customLayout=${RETURN_STATUS['PASS_QC'].value}&filters=${encodeURIComponent(
      //     JSON.stringify([FILTERS_OBJECT['PASS_QC']], null, 0),
      //   )}`,
      //   isDefault: true,
      // },
      {
        rootPath: '/orders-return',
        pathId: RETURN_STATUS['FAILED_QC'].value,
        title: 'Failed QC',
        customPath: `/orders-return?customLayout=${RETURN_STATUS['FAILED_QC'].value}&filters=${encodeURIComponent(
          JSON.stringify([FILTERS_OBJECT['FAILED_QC']], null, 0),
        )}`,
        isDefault: true,
      },
      {
        rootPath: '/orders-return',
        pathId: RETURN_STATUS['WAITING_FOR_REFUND'].value,
        title: 'Waiting for refund',
        customPath: `/orders-return?customLayout=${RETURN_STATUS['WAITING_FOR_REFUND'].value}&filters=${encodeURIComponent(
          JSON.stringify([FILTERS_OBJECT['WAITING_FOR_REFUND']], null, 0),
        )}`,
        isDefault: true,
      },
      {
        rootPath: '/orders-return',
        pathId: RETURN_STATUS['COMPLETED'].value,
        title: 'Completed',
        customPath: `/orders-return?customLayout=${RETURN_STATUS['COMPLETED'].value}&filters=${encodeURIComponent(
          JSON.stringify([FILTERS_OBJECT['COMPLETED']], null, 0),
        )}`,
        isDefault: true,
      },
      {
        rootPath: '/orders-return',
        pathId: RETURN_STATUS['CANCELLED'].value,
        title: 'Cancelled',
        customPath: `/orders-return?customLayout=${RETURN_STATUS['CANCELLED'].value}&filters=${encodeURIComponent(
          JSON.stringify([FILTERS_OBJECT['CANCELLED']], null, 0),
        )}`,
        isDefault: true,
      },
    ];
  }, []);

  const filters: TFilterItem[] = useMemo(() => {
    return [
      {
        key: 'status',
        label: 'Status',
        dataSource: Object.values(RETURN_STATUS)
          ?.filter((e) => e?.value !== 'PASS_QC')
          ?.map((e: DynamicObjectType) => ({
            value: e.value,
            label: t(e.text),
          })),
      },
      {
        key: 'createdAt',
        label: 'Created time',
        isDatePicker: true,
      },
      {
        key: 'cancelledTime',
        label: 'Request canceled time',
        isDatePicker: true,
      },
      {
        key: 'completedTime',
        label: 'Request completed time',
        isDatePicker: true,
      },
      {
        key: 'failedQCReason',
        label: 'Failed QC reason',
        dataSource: Object.values(FAILED_QC_REASON).map(
          (e: DynamicObjectType) => ({
            value: e.value,
            label: t(e.label),
          }),
        ),
      },
      {
        key: 'order.channel',
        label: 'Channel',
        dataSource: CHANNELS_FILTER.map((e: DynamicObjectType) => ({
          value: e.value,
          label: t(e.label),
        })),
      },
      {
        key: 'returnType',
        label: 'Return type',
        dataSource: Object.values(RETURN_CHANNEL),
      },
      {
        key: 'order.location.id',
        label: 'From Warehouse',
        valueCPN: <LocationsFilter />,
      },
      {
        key: 'returnWarehouse.id',
        label: 'To Warehouse',
        valueCPN: <LocationsFilter />,
      },
      {
        key: 'returnMethod',
        label: 'Return Method',
        dataSource: Object.values(RETURN_METHOD).map(
          (e: DynamicObjectType) => ({
            value: e.value,
            label: t(e.text),
          }),
        ),
      },
      {
        key: 'exchangeId',
        label: 'Is Exchange',
        isCheckNull: true,
        dataSource: [
          {
            value: true,
            label: 'Yes',
          },
          {
            value: false,
            label: 'No',
          },
        ],
      },
      {
        key: 'reason',
        label: 'Return Reason',
        dataSource: Object.values(RETURN_REASON).map(
          (e: DynamicObjectType) => ({
            value: e.value,
            label: t(e.text),
          }),
        ),
      },
      {
        key: 'order.paymentType',
        label: 'Payment Method',
        valueCPN: (
          <PaymentTypeFilter
            reference={{
              model: 'payment-method',
              valueProp: 'name',
              labelFormat: (e) => upperFirst(e?.name || ''),
              initFilters: {
                filters: [
                  {
                    condition: 'and',
                    key: 'isActive',
                    operator: 'eq',
                    value: true,
                  },
                ],
              },
            }}
          />
        ),
      },
    ];
  }, [t]);

  const restInitialFilter: FilterFromUrlResponseType = useMemo(() => {
    return {
      join: [
        'location',
        'payments',
        'customer',
        'order.location',
        'returnWarehouse',
      ],
      orderBy: 'createdAt,ASC',
      filters: [
        searchParams.get('statusOms')
          ? {
              condition: 'and',
              key: 'status',
              operator: 'is',
              value: searchParams.get('statusOms'),
            }
          : {},
        !filter?.filters?.find((item) => item.key === 'createdAt')
          ? {
              condition: 'and',
              key: 'createdAt',
              operator: 'gte',
              value: dayjs()
                .subtract(1, 'month')
                .startOf('month')
                .toISOString(),
            }
          : {},
        !filter?.filters?.find((item) => item.key === 'createdAt')
          ? {
              condition: 'and',
              key: 'createdAt',
              operator: 'lte',
              value: dayjs().toISOString(),
            }
          : {},
      ],
    };
  }, [filter, searchParams]);

  const getSearchKey = useMemo(() => {
    switch (searchParams.get('fieldSearch')) {
      case 'returnNumber':
        return ['returnNumber'];
      case 'orderNumber':
        return ['orderNumber'];
      // case 'saleOrderId':
      //    return ['order.saleOrderId'];
      case 'sku':
        return ['sku'];
      case 'telephone':
        return ['telephone'];
      case 'email':
        return ['email'];
      case 'customerName':
        return ['customerName'];
      case 'deliveryCode':
        return ['deliveryCode'];
      default:
        return [
          'returnNumber',
          'orderNumber',
          // 'order.saleOrderId',
          'sku',
          'telephone',
          'email',
          'customer',
          'deliveryCode',
        ];
    }
  }, [searchParams.get('fieldSearch')]);

  const columns = useMemo(() => {
    return getColumns(searchParams.get('customLayout')) as IRestColumnProps[];
  }, [searchParams.get('customLayout')]);

  const exportExcelQuery = (restInitialFilter, filter) => {
    const filterData = {
      filters: [
        ...values(restInitialFilter?.filters || {}),
        ...values(filter.filters || {}),
      ],
      orderBy: 'createdAt',
    };
    const requestParams = getQueryString(
      convertRequestParams(
        'GET_ALL',
        omit(filterData, ['join']) as DynamicObjectType,
      ),
    );
    return `/return-orders/export/bulk?${requestParams?.replace('page=1&limit=10', '')}`;
  };

  const hasExport = useMemo(
    () =>
      newCheckRole(
        '/return-orders/export/bulk',
        'GET',
        user?.roles,
        user?.isSuperUser,
      ),
    [user],
  );

  const getFilter = useMemo(() => {
    if (searchParams.get('customLayout') === RETURN_STATUS['APPROVED'].value) {
      return [
        ...filters,
        {
          key: 'physicalGoodsReceived',
          label: 'Marked as Received',
          dataSource: [
            {
              value: 'true',
              label: 'Yes',
            },
            {
              value: 'false',
              label: 'No',
            },
          ],
        },
      ];
    }
    return filters;
  }, [searchParams.get('customLayout'), filters]);

  return (
    <RestList
      initialFilter={restInitialFilter}
      columns={columns}
      model="ordersReturn"
      filters={getFilter}
      // customActions={[<ImportOrders key="0" />]}
      gotoCreatePage={() => navigate('/orders-return/create')}
      searchKeys={getSearchKey}
      // extraActionFilters={extraActionsFilter}
      placeholderInputSearch="Search ..."
      rowSelectionActions={ROW_SELECTION_ACTIONS}
      hasImport={false}
      hasExport={hasExport}
      hasCustomLayout
      initialLayouts={INITIAL_LAYOUTS}
      dataFieldSearch={DATA_FIELD_SEARCH}
      extraActionFilters={EXTRA_ACTIONS_FILTER}
      exportExcelQuery={`${exportExcelQuery(restInitialFilter, filter)}`}
      excelName={`Export_return_orders_${dayjs().format('YYYY_MM_DD')}`}
    />
  );
}
